import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  Users, DollarSign, Plus, Edit, Trash2, Eye,
  RefreshCw, MoreHorizontal, HelpCircle, ArrowUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import PreContractService from '../../services/preContractService';

const DoAActionDropdown = ({ row, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => setIsOpen(!isOpen);

  const dropdownMenu = isOpen && buttonRef.current ? (
    <ul className="dropdown-menu dropdown-menu-end show" style={{
      position: 'fixed',
      top: `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
      left: `${buttonRef.current.getBoundingClientRect().left}px`,
      zIndex: 999999,
      minWidth: '160px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      backgroundColor: '#ffffff',
      listStyle: 'none',
      padding: '0'
    }}>
      <li>
        <button className="dropdown-item d-flex align-items-center" onClick={() => { setIsOpen(false); onView(row); }} style={{ padding: '10px 16px', border: 'none', width: '100%', textAlign: 'left', background: 'none' }}>
          <Eye size={16} className="me-2 text-muted" /> View
        </button>
      </li>
      <li>
        <button className="dropdown-item d-flex align-items-center" onClick={() => { setIsOpen(false); onEdit(row); }} style={{ padding: '10px 16px', border: 'none', width: '100%', textAlign: 'left', background: 'none' }}>
          <Edit size={16} className="me-2 text-muted" /> Edit Rule
        </button>
      </li>
      <li className="border-top">
        <button className="dropdown-item d-flex align-items-center text-danger" onClick={() => { setIsOpen(false); onDelete(row); }} style={{ padding: '10px 16px', border: 'none', width: '100%', textAlign: 'left', background: 'none' }}>
          <Trash2 size={16} className="me-2" /> Delete
        </button>
      </li>
    </ul>
  ) : null;

  return (
    <>
      <div ref={dropdownRef} className="dropdown">
        <button
          ref={buttonRef}
          className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
          type="button"
          onClick={handleToggle}
          style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto', width: '32px', height: '32px' }}
        >
          <MoreHorizontal size={16} className="text-blue-500" />
        </button>
      </div>
      {isOpen && createPortal(dropdownMenu, document.body)}
    </>
  );
};

const DoAMatrix = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  const loadRules = async () => {
    setLoading(true);
    try {
      const response = await PreContractService.getDoAList();
      const list = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
      setRules(list);
    } catch (err) {
      console.error(err);
      showError('Failed to load DoA rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleDeleteConfirm = async () => {
    try {
        if (ruleToDelete && ruleToDelete._id) {
            await PreContractService.deleteDoA(ruleToDelete._id);
            success('Rule deleted successfully');
            loadRules();
        }
    } catch (err) {
        showError('Failed to delete rule');
    } finally {
        setShowDeleteModal(false);
        setRuleToDelete(null);
    }
  };

  const headerActions = [
    { label: '', icon: <HelpCircle size={18} />, onClick: () => {}, variant: 'outline' },
    { label: '', icon: <RefreshCw size={18} />, onClick: loadRules, variant: 'outline' },
    { label: 'New Rule', icon: <Plus size={18} />, onClick: () => navigate('/contracts/pre-contract/doa/create'), variant: 'primary' }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader title="DoA Matrix" description="Manage role-based delegation of authority limits" actions={headerActions} />
      <div className="page-container">
        <DataTable
          showActions={false}
          data={rules}
          columns={[
            {
              key: '__spacer__',
              label: '',
              width: '20px',
              sortable: false,
              render: () => <div style={{ width: '20px' }} />
            },
            {
              key: 'rank',
              label: 'Rank',
              width: '10%',
              render: (value) => (
                <div className="badge bg-light text-secondary border">Level {value}</div>
              )
            },
            {
              key: 'role',
              label: 'Role / Title',
              width: '30%',
              render: (value) => (
                <div className="d-flex align-items-center gap-2">
                    <Users size={16} className="text-primary" />
                    <span className="fw-medium">{value}</span>
                </div>
              )
            },
            {
                key: 'approvalLimit',
                label: 'Max Approval Limit',
                width: '30%',
                render: (value, row) => (
                    <div className="d-flex align-items-center gap-2 text-success fw-bold">
                        <DollarSign size={14} />
                        <span>{row.currency} {value.toLocaleString()}</span>
                    </div>
                )
            },
            {
                key: 'isActive',
                label: 'Status',
                width: '15%',
                render: (value) => (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <div className={`dot ${value ? 'bg-success' : 'bg-secondary'}`} style={{ width: 8, height: 8, borderRadius: '50%' }}></div>
                    <span style={{ textTransform: 'capitalize' }}>{value ? 'Active' : 'Inactive'}</span>
                  </div>
                )
            },
            {
              key: 'actions',
              label: 'Actions',
              width: '15%',
              render: (value, row) => (
                <div className="d-flex justify-content-center">
                    <DoAActionDropdown 
                        row={row} 
                        onView={() => navigate(`/contracts/pre-contract/doa/view/${row._id}`)} 
                        onEdit={() => navigate(`/contracts/pre-contract/doa/edit/${row._id}`)} 
                        onDelete={(r) => { setRuleToDelete(r); setShowDeleteModal(true); }} 
                    />
                </div>
              )
            }
          ]}
          loading={loading}
          emptyMessage="No DoA rules defined. Add a role to get started."
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Rule"
        message={`Delete authority rule for "${ruleToDelete?.role}"?`}
        confirmText="Delete"
        confirmVariant="danger"
      />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default DoAMatrix;